import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Fade from "@mui/material/Fade";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { setUserEntry } from "../../server/Customer";
import { isEmailVerificationPopupOpen } from "../../utils/emailVerification";
import NewAddress from "../addressShipping/NewAddress";
import UserLogin from "./UserLogin";
import UserSighIn from "./UserSighin";
import * as actionType from "../../store/action";

const TAB = {
  login: "login",
  signIn: "signIn",
};

function normalizeTab(value) {
  return value === TAB.signIn ? TAB.signIn : TAB.login;
}

export default function AuthDialog() {
  const dispatch = useDispatch();
  const userState = useSelector((state) => state.user);
  const { userEntry } = userState;

  const open =
    Boolean(userEntry?.[0]) &&
    !isEmailVerificationPopupOpen({ user: userState });
  const initialTab = normalizeTab(userEntry?.[1]);
  const [tab, setTab] = useState(initialTab);

  const [error, setError] = useState(null); // { title?: string, message: string }

  // Register flow keeps the current UX: address check before showing the sign-up form.
  const [registerStep, setRegisterStep] = useState("address"); // "address" | "form"

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setError(null);
    if (initialTab === TAB.signIn) {
      setRegisterStep("address");
      try {
        localStorage.removeItem("tempStation");
      } catch {
        /* ignore */
      }
    }
  }, [open, initialTab]);

  const close = () => {
    setError(null);
    setRegisterStep("address");
    dispatch(setUserEntry([false, TAB.login]));
  };

  const headerTitle = useMemo(() => {
    return tab === TAB.signIn ? "הרשמה לאתר" : "כניסה לאתר";
  }, [tab]);

  const switchTab = (next) => {
    const nextTab = normalizeTab(next);
    if (nextTab === tab) return;
    setError(null);
    setTab(nextTab);
    if (nextTab === TAB.signIn) {
      setRegisterStep("address");
      try {
        localStorage.removeItem("tempStation");
      } catch {
        /* ignore */
      }
    }
  };

  const onTabChange = (_e, next) => {
    switchTab(next);
  };

  const handleSignupAddressSaved = (addressData) => {
    try {
      localStorage.setItem("tempStation", JSON.stringify(addressData));
    } catch {
      /* ignore */
    }
    dispatch({
      type: actionType.SET_TEMP_ADDRESS,
      payload: addressData.polygonId,
    });
    setRegisterStep("form");
    setError(null);
  };

  const showInlineError = (message, title) => {
    if (!message) return;
    setError({ title, message });
  };

  const maybeSuggestRegister = tab === TAB.login;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      disablePortal={false}
      aria-labelledby="auth-dialog-title"
      className="hz-auth-dialog"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(11, 31, 58, 0.38)",
            backdropFilter: "blur(8px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "var(--hz-radius-xl, 32px)",
            overflow: "hidden",
            background: "transparent",
            boxShadow: "none",
          },
        },
      }}
      sx={{
        "& .MuiDialogContent-root": { padding: 0 },
        "& .MuiDialog-container": {
          /* Appear below the header/categories stack */
          alignItems: "flex-start",
          padding: "16px",
          paddingTop: "calc(var(--shell-fixed-stack-bottom, 170px) + 16px)",
          paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
          boxSizing: "border-box",
        },
      }}
    >
      <Box dir="rtl" sx={{ position: "relative" }}>
        <IconButton
          onClick={close}
          aria-label="סגור"
          disableRipple
          sx={{
            position: "absolute",
            top: 14,
            left: 14,
            width: "var(--tap-size, 44px)",
            height: "var(--tap-size, 44px)",
            borderRadius: 999,
            background: "transparent",
            color: "var(--hz-muted, var(--hz-muted))",
            zIndex: 2,
            transition: "color 0.18s ease",
            "& svg": {
              transition: "transform 0.18s ease",
            },
            "&:hover": {
              background: "transparent",
              color: "var(--hz-green-hover, var(--hz-green-hover))",
              "& svg": {
                transform: "scale(1.12)",
              },
            },
          }}
        >
          <X size={22} strokeWidth={2} aria-hidden />
        </IconButton>

        <div className="hz-auth-dialog__card">
          <div className="user-entry">
            <div className="user-entry-header hz-auth-dialog__header">
              <h3 id="auth-dialog-title">{headerTitle}</h3>
              <p className="user-entry-subtitle">
                {tab === TAB.login
                  ? "ברוך הבא — התחבר כדי להמשיך לקנייה."
                  : "כמה פרטים קצרים ויוצאים לדרך"}
              </p>

              <Tabs
                value={tab}
                onChange={onTabChange}
                variant="fullWidth"
                textColor="inherit"
                sx={{
                  mt: 2,
                  minHeight: 44,
                  "& .MuiTabs-flexContainer": { gap: 0 },
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: 999,
                    backgroundColor: "var(--hz-cta, var(--hz-cta))",
                  },
                  "& .MuiTab-root": {
                    minHeight: 44,
                    fontWeight: 800,
                    fontSize: 14,
                    color: "var(--hz-muted, var(--hz-muted))",
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: "var(--hz-cta, var(--hz-cta))",
                  },
                }}
              >
                <Tab value={TAB.login} label="כניסה לאתר" />
                <Tab value={TAB.signIn} label="הרשמה לאתר" />
              </Tabs>
            </div>

            <DialogContent className="hz-auth-dialog__body">
              <div className="hz-auth-dialog__inline-error-slot">
                <Fade in={Boolean(error)} timeout={180} unmountOnExit>
                  <Box sx={{ px: 3, pt: 2 }}>
                    <Alert
                      severity="error"
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: "rgba(220, 38, 38, 0.45)",
                        background: "rgba(220, 38, 38, 0.06)",
                        color: "rgba(127, 29, 29, 1)",
                        "& .MuiAlert-icon": {
                          color: "rgba(220, 38, 38, 0.95)",
                        },
                      }}
                    >
                      {error?.title ? (
                        <AlertTitle>{error.title}</AlertTitle>
                      ) : null}
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                        {error?.message}
                      </Typography>
                      {maybeSuggestRegister ? (
                        <Box sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            onClick={() => switchTab(TAB.signIn)}
                            sx={{
                              px: 0,
                              minWidth: "unset",
                              textDecoration: "underline",
                              fontWeight: 800,
                              color:
                                "var(--hz-green-hover, var(--hz-green-hover))",
                              "&:hover": { background: "transparent" },
                            }}
                          >
                            עדיין אין לך חשבון? להרשמה לאתר
                          </Button>
                        </Box>
                      ) : null}
                    </Alert>
                  </Box>
                </Fade>
              </div>

              <div className="hz-auth-dialog__panel-slot">
                <Fade key={`${tab}-${registerStep}`} in timeout={220}>
                  <Box>
                    {tab === TAB.login ? (
                      <UserLogin
                        onInlineError={(msg, title) =>
                          showInlineError(msg, title)
                        }
                        onClearInlineError={() => setError(null)}
                        onSwitchToRegister={() => switchTab(TAB.signIn)}
                      />
                    ) : registerStep === "address" ? (
                      <NewAddress
                        pushAddress={handleSignupAddressSaved}
                        submitLabel="המשך > "
                      />
                    ) : (
                      <UserSighIn
                        parentName="signIn"
                        onInlineError={(msg, title) =>
                          showInlineError(msg, title)
                        }
                        onClearInlineError={() => setError(null)}
                        onRegistered={() => close()}
                      />
                    )}
                  </Box>
                </Fade>
              </div>
            </DialogContent>
          </div>
        </div>
      </Box>
    </Dialog>
  );
}
